import React, { useState } from "react";
import OutputChoose from "./outputStorageChoose";
import { fetchFiles } from "../Utility/PDBStorage/PDBStorageUtils";
import { retrieveMineData } from "../Utility/ConsumeToken/consumeToken";

const sortByDateDesc = (arr) =>
  [...arr].sort((a, b) => {
    const ta = a?.date ? new Date(a.date).getTime() : 0;
    const tb = b?.date ? new Date(b.date).getTime() : 0;
    return tb - ta;
  });

// helper per ricavare il PDB ID dal nome file
const fileNameToPdbId = (fileName) => {
  if (!fileName) return "";
  const upper = fileName.toUpperCase();
  return upper.endsWith(".PDB") ? upper.slice(0, -4) : upper;
};

/**
 * Loader per DownloadPDB Storage:
 * - single-select: onLoad(pdbId: string)
 * - multi-select: onLoad(pdbIds: string[])
 */
const PdbStorageLoader = ({
  buttonLabel = "Choose from DownloadPDB Storage",
  buttonClassName = "sign-button button download-button",
  textAction = "Use in CleanPDB",
  popupMaxWidth = 1000,
  multiSelect = false,
  onLoad,                 // single: (pdbId) => void, multi: (pdbIds[]) => void
  selectedIds = [],       // PDB IDs già presenti (es. dalla textarea)
  maxSelection = 10,      // numero massimo selezionabili
}) => {
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [storageFiles, setStorageFiles] = useState([]);
  const [isLoadingStorage, setIsLoadingStorage] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]); // filenames selezionati (solo multi)

  const openStorageModal = async () => {
    try {
      setIsLoadingStorage(true);
      setShowStorageModal(true);

      const apitoken = await retrieveMineData();
      const fileList = await fetchFiles(apitoken._id, "CLEAN_PDB");

      const files =
        Array.isArray(fileList) ? fileList
        : Array.isArray(fileList?.files) ? fileList.files
        : Array.isArray(fileList?.data) ? fileList.data
        : [];

      const normalized = files.map((f) => ({
        ...f,
        filename: f.filename ?? f.name ?? f.fileName,
        date: f.date ?? f.updatedAt ?? f.timestamp ?? f.lastModified ?? null,
      }));

      const sorted = sortByDateDesc(normalized);
      setStorageFiles(sorted);

      // 🔹 Pre-seleziona i file in base a selectedIds (solo multiSelect)
      if (multiSelect && selectedIds && selectedIds.length > 0) {
        const idSet = new Set(
          selectedIds.map((id) => id.trim().toUpperCase()).filter(Boolean)
        );

        const preSelected = sorted
          .filter((f) => idSet.has(fileNameToPdbId(f.filename)))
          .slice(0, maxSelection) // rispetta maxSelection anche in pre-fill
          .map((f) => f.filename);

        setSelectedFiles(preSelected);
      } else {
        setSelectedFiles([]); // nessuna preselezione
      }
    } catch (err) {
      console.error("Error loading files from DownloadPDB Storage:", err);
      setStorageFiles([]);
      setSelectedFiles([]);
    } finally {
      setIsLoadingStorage(false);
    }
  };

  // SINGLE SELECT: click diretto
  const handleChooseSinglePDBID = (fileName) => {
    setShowStorageModal(false);
    if (onLoad && fileName) {
      const pdbId = fileNameToPdbId(fileName);
      onLoad(pdbId);
    }
  };

  // SINGLE SELECT FILE: click diretto
  const handleChooseSingleFile = (fileName) => {
    setShowStorageModal(false);
    if (onLoad && fileName) {
      // Passo direttamente il filename, es. "1ABC_clean.pdb"
      onLoad(fileName);
    }
  };

  // MULTI SELECT: toggle file
  const handleToggleFile = (fileName) => {
    setSelectedFiles((prev) => {
      const already = prev.includes(fileName);
      if (already) {
        // deselezione
        return prev.filter((f) => f !== fileName);
      }
      // nuova selezione: rispetta il limite
      if (prev.length >= maxSelection) {
        alert(`You can select at most ${maxSelection} files.`);
        return prev;
      }
      return [...prev, fileName];
    });
  };

  const handleConfirmMulti = () => {
    if (!onLoad || selectedFiles.length === 0) return;
    const pdbIds = selectedFiles.map(fileNameToPdbId);
    onLoad(pdbIds);
    setShowStorageModal(false);
    setSelectedFiles([]);
  };

  const handleCloseModal = () => {
    setShowStorageModal(false);
    setSelectedFiles([]); // lo stato sarà ricalcolato alla prossima apertura in base a selectedIds
  };

  return (
    <>
      <button
        className={buttonClassName}
        type="button"
        onClick={openStorageModal}
      >
        {buttonLabel}
      </button>

      {showStorageModal && (
        <div className="popup-overlay">
          <div
            className="popup-content"
            style={{
              maxWidth: popupMaxWidth,
              width: "95%",
              display: "flex",
              flexDirection: "column",
              maxHeight: "80vh",
            }}
          >
            <div className="divCloseButton">
              <button onClick={handleCloseModal} className="CloseButton text">
                X
              </button>
            </div>
            <p className="text" style={{ textAlign: "center" }}>
              {multiSelect
                ? "Choose one or more files from DownloadPDB Storage to use in CleanPDB"
                : "Choose a file from DownloadPDB Storage to use in CleanPDB"}
            </p>
            <div className="space"></div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
                padding: "0 2px",
              }}
            >
              <span className="textlink">
                Files:{" "}
                <b>{Array.isArray(storageFiles) ? storageFiles.length : 0}</b>
              </span>
              <span className="text" style={{ opacity: 0.7 }}>
                {isLoadingStorage ? "Loading…" : " "}
              </span>
            </div>

            <div
              style={{
                flex: "1 1 auto",
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 8,
                background: "#fff",
              }}
            >
              {isLoadingStorage ? (
                <p>Loading...</p>
              ) : Array.isArray(storageFiles) && storageFiles.length > 0 ? (
                storageFiles.map((fileItem, idx) => {
                  const isSelected = selectedFiles.includes(fileItem.filename);

                  return (
                    <OutputChoose
                      key={`${fileItem.filename}-${idx}`}
                      name={fileItem.filename}
                      date={fileItem.date}
                      textAction={textAction}
                      // single: bottone
                      selectable={multiSelect}
                      selected={multiSelect ? isSelected : false}
                      action={
                        !multiSelect
                          ? () => handleChooseSingleFile(fileItem.filename)
                          : undefined
                      }
                      onToggle={
                        multiSelect
                          ? () => handleToggleFile(fileItem.filename)
                          : undefined
                      }
                    />
                  );
                })
              ) : (
                <p>Files not found</p>
              )}
            </div>

            {multiSelect && (
              <div style={{ marginTop: 12, textAlign: "right" }}>
                <button
                  className="sign-button button"
                  type="button"
                  disabled={selectedFiles.length === 0}
                  onClick={handleConfirmMulti}
                >
                  Use selected files ({selectedFiles.length}/{maxSelection})
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PdbStorageLoader;
